import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import { LazyPage } from "@/shared/ui/LazyPage";
import { CompetitionAdminLayout } from "./layout/CompetitionAdminLayout";
import { CompetitionLayout } from "./layout/CompetitionLayout";

// ─── Lazy page imports ────────────────────────────────────────────────────────

const RootLayout = lazy(() =>
  import("./layout/RootLayout").then((m) => ({ default: m.RootLayout })),
);

const ErrorBoundary = lazy(() =>
  import("./error/ErrorBoundary").then((m) => ({ default: m.ErrorBoundary })),
);

const PinLoginPage = lazy(() =>
  import("@/modules/auth/presentation/pages/PinLoginPage").then((m) => ({
    default: m.PinLoginPage,
  })),
);

const JudgePinLoginPage = lazy(() =>
  import("@/modules/auth/presentation/pages/JudgePinLoginPage").then((m) => ({
    default: m.JudgePinLoginPage,
  })),
);

const EventsListPage = lazy(() =>
  import("@/modules/competition/presentation/pages/EventsListPage").then(
    (m) => ({
      default: m.EventsListPage,
    }),
  ),
);

const EventAdminWorkspacePage = lazy(() =>
  import(
    "@/modules/competition/presentation/pages/EventAdminWorkspacePage"
  ).then((m) => ({
    default: m.EventAdminWorkspacePage,
  })),
);

const ResultsPage = lazy(() =>
  import("@/modules/competition/presentation/pages/ResultsPage").then((m) => ({
    default: m.ResultsPage,
  })),
);

const PublicEventsPage = lazy(() =>
  import("@/modules/competition/presentation/pages/PublicEventsPage").then(
    (m) => ({
      default: m.PublicEventsPage,
    }),
  ),
);

const CompetitionJudgeEventPage = lazy(() =>
  import("./layout/CompetitionJudgeEventPage").then((m) => ({
    default: m.CompetitionJudgeEventPage,
  })),
);

// ─── Root ─────────────────────────────────────────────────────────────────────

const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: ErrorBoundary,
});

// "/" → redirect to competition login
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => redirect({ to: "/competition/login" }),
});

// ─── Competition Routes ───────────────────────────────────────────────────────

const competitionLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/competition/login",
  component: () => (
    <LazyPage>
      <PinLoginPage />
    </LazyPage>
  ),
});

const competitionJudgePinLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/competition/judge-login/$eventId/$judgeNumber",
  beforeLoad: ({ params }) => {
    if (!["1", "2", "3"].includes(params.judgeNumber)) {
      throw redirect({ to: "/competition/login" });
    }
  },
  component: () => (
    <LazyPage>
      <JudgePinLoginPage />
    </LazyPage>
  ),
});

/** Legacy URL → public events catalog */
const competitionResultsRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/competition/results",
  beforeLoad: () => redirect({ to: "/competition/events" }),
});

/** Published events directory (no competition auth layout) */
const competitionPublicEventsCatalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/competition/events",
  component: () => (
    <LazyPage>
      <PublicEventsPage />
    </LazyPage>
  ),
});

const competitionPublicEventResultsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/competition/events/$eventId/results",
  component: () => (
    <LazyPage>
      <ResultsPage />
    </LazyPage>
  ),
});

// Protected competition routes (CompetitionLayout checks auth)
const competitionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/competition",
  component: CompetitionLayout,
});

// Admin
const competitionAdminParentRoute = createRoute({
  getParentRoute: () => competitionRoute,
  path: "/admin",
  component: CompetitionAdminLayout,
});

const competitionAdminRoute = createRoute({
  getParentRoute: () => competitionAdminParentRoute,
  path: "/",
  component: () => (
    <LazyPage>
      <EventsListPage />
    </LazyPage>
  ),
});

/** Admin workspace for one event: `/competition/events/:eventId/admin` */
const competitionEventAdminWorkspaceRoute = createRoute({
  getParentRoute: () => competitionRoute,
  path: "events/$eventId/admin",
  component: CompetitionAdminLayout,
});

const competitionEventAdminWorkspaceIndexRoute = createRoute({
  getParentRoute: () => competitionEventAdminWorkspaceRoute,
  path: "/",
  component: () => (
    <LazyPage>
      <EventAdminWorkspacePage />
    </LazyPage>
  ),
});

const competitionJudgeEventRoute = createRoute({
  getParentRoute: () => competitionRoute,
  path: "/events/$eventId/judge/$judgeNumber",
  beforeLoad: ({ params }) => {
    if (!["1", "2", "3"].includes(params.judgeNumber)) {
      throw redirect({ to: "/competition/login" });
    }
  },
  component: () => (
    <LazyPage>
      <CompetitionJudgeEventPage />
    </LazyPage>
  ),
});

// ─── Route tree ───────────────────────────────────────────────────────────────

export const routeTree = rootRoute.addChildren([
  indexRoute,
  competitionLoginRoute,
  competitionJudgePinLoginRoute,
  competitionResultsRedirectRoute,
  competitionPublicEventsCatalogRoute,
  competitionPublicEventResultsRoute,
  competitionRoute.addChildren([
    competitionAdminParentRoute.addChildren([competitionAdminRoute]),
    competitionEventAdminWorkspaceRoute.addChildren([
      competitionEventAdminWorkspaceIndexRoute,
    ]),
    competitionJudgeEventRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
